import * as React from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/**
 * Combobox - A searchable, scrollable single-select dropdown component
 * 
 * Features:
 * - Search/filter options
 * - Mouse wheel scrolling support
 * - Keyboard navigation
 * 
 * To add multi-select capability:
 * 1. Change value prop from `string` to `string[]`
 * 2. Update onValueChange to handle array of values
 * 3. Add logic to toggle selection instead of replacing
 * 4. Display selected count in trigger button
 * 5. See MultiSelect component for reference implementation
 */

export interface ComboboxOption {
  value: string;
  label: string;
  [key: string]: any; // Allow additional properties for custom rendering
}

interface ComboboxProps {
  options: ComboboxOption[];
  value?: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  emptyMessage?: string;
  searchPlaceholder?: string;
  className?: string;
  disabled?: boolean;
  renderOption?: (option: ComboboxOption) => React.ReactNode;
}

export function Combobox({
  options,
  value,
  onValueChange,
  placeholder = "Select option...",
  emptyMessage = "No option found.",
  searchPlaceholder = "Search...",
  className,
  disabled = false,
  renderOption,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);

  // Handle both string and number values for comparison
  const selectedOption = options.find((option) => String(option.value) === String(value));

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn(
            "w-full justify-between",
            !value && "text-muted-foreground",
            className
          )}
          disabled={disabled}
        >
          <span className="truncate">
            {selectedOption ? selectedOption.label : placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder={searchPlaceholder} className="h-9" />
          <CommandList>
            <CommandEmpty>{emptyMessage}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={String(option.value)}
                  value={String(option.value)}
                  onSelect={(currentValue) => {
                    // cmdk lowercases the value in onSelect, so we need to find the original option
                    // to get the correct case for the value (handles both string and number values)
                    const selectedOption = options.find(
                      (opt) => String(opt.value).toLowerCase() === currentValue.toLowerCase()
                    );
                    const originalValue = selectedOption?.value || currentValue;
                    // Always convert to string to match the expected type for form validation
                    const stringValue = String(originalValue);
                    onValueChange(stringValue === String(value) ? "" : stringValue);
                    setOpen(false);
                  }}
                  className="flex items-center gap-2"
                >
                  <div className="flex-1 min-w-0">
                    {renderOption ? renderOption(option) : option.label}
                  </div>
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0",
                      String(value) === String(option.value) ? "opacity-100" : "opacity-0"
                    )}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
